import React, { useState, useEffect } from 'react';
import { useSearchParams, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Calendar, Clock, MapPin, Users, Star, ChevronRight, Music, Drum, Footprints, CheckCircle, X, BookOpen, Globe, Award, Heart, ChevronDown, Home } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { PageEditable } from '@/components/edit/PageEditable';
import InlineClassField from '@/components/edit/InlineClassField';
import { useHero } from '@/hooks/useHero';

const API = process.env.REACT_APP_BACKEND_URL;

// Group classes by category
const groupClassesByCategory = (classes) => {
  const grouped = {
    adult: [],
    kids: [],
    private: []
  };
  classes.forEach(cls => {
    if (grouped[cls.category]) {
      grouped[cls.category].push(cls);
    } else {
      grouped.adult.push(cls);
    }
  });
  return grouped;
};

// Order days properly
const dayOrder = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];
const sortByDay = (classes) => {
  return [...classes].sort((a, b) => dayOrder.indexOf(a.day_of_week) - dayOrder.indexOf(b.day_of_week));
};

const getLevelBadgeColor = (level) => {
  const colors = {
    beginner: 'bg-green-500/20 text-green-400',
    intermediate: 'bg-yellow-500/20 text-yellow-400',
    advanced: 'bg-red-500/20 text-red-400',
    all: 'bg-blue-500/20 text-blue-400'
  };
  return colors[level] || colors.all;
};

const LEVEL_OPTIONS = [
  { value: 'beginner', label: 'Beginner' },
  { value: 'intermediate', label: 'Intermediate' },
  { value: 'advanced', label: 'Advanced' },
  { value: 'all', label: 'All Levels' },
];

const DAY_OPTIONS = [
  { value: 'Monday', label: 'Monday' },
  { value: 'Tuesday', label: 'Tuesday' },
  { value: 'Wednesday', label: 'Wednesday' },
  { value: 'Thursday', label: 'Thursday' },
  { value: 'Friday', label: 'Friday' },
  { value: 'Saturday', label: 'Saturday' },
  { value: 'Sunday', label: 'Sunday' },
];

const ClassCard = ({ danceClass, onRegister }) => {
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="bg-card border border-border/50 rounded-2xl overflow-hidden hover:border-primary/50 transition-all group"
    >
      <div className="p-6">
        {/* Header */}
        <div className="flex items-start justify-between mb-4 gap-3">
          <div className="flex-1 min-w-0">
            <h3 className="text-xl font-bold font-display text-foreground mb-1">
              <InlineClassField
                classId={danceClass.id}
                field="name"
                value={danceClass.name}
                inputClassName="w-full text-xl font-bold"
              />
            </h3>
            <p className="text-sm text-muted-foreground">
              <InlineClassField
                classId={danceClass.id}
                field="instructor"
                value={danceClass.instructor}
                inputClassName="w-full text-sm"
              />
            </p>
          </div>
          <span className={`px-3 py-1 rounded-full text-xs font-bold whitespace-nowrap ${getLevelBadgeColor(danceClass.level)}`}>
            <InlineClassField
              classId={danceClass.id}
              field="level"
              value={danceClass.level}
              options={LEVEL_OPTIONS}
              inputClassName="text-xs"
            />
          </span>
        </div>

        {/* Description */}
        {(danceClass.description || true) && (
          <p className="text-sm text-muted-foreground mb-4 line-clamp-2">
            <InlineClassField
              classId={danceClass.id}
              field="description"
              value={danceClass.description}
              multiline
              placeholder="Class description"
            />
          </p>
        )}

        {/* Schedule Info */}
        <div className="space-y-2 mb-4">
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <Calendar className="w-4 h-4 text-primary" />
            <span className="font-medium text-foreground">
              <InlineClassField
                classId={danceClass.id}
                field="day_of_week"
                value={danceClass.day_of_week}
                options={DAY_OPTIONS}
              />
            </span>
          </div>
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <Clock className="w-4 h-4 text-primary" />
            <span className="inline-flex items-center gap-1">
              <InlineClassField
                classId={danceClass.id}
                field="start_time"
                value={danceClass.start_time}
                inputClassName="w-24 text-sm"
              />
              <span>-</span>
              <InlineClassField
                classId={danceClass.id}
                field="end_time"
                value={danceClass.end_time}
                inputClassName="w-24 text-sm"
              />
            </span>
          </div>
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <MapPin className="w-4 h-4 text-primary" />
            <span className="truncate flex-1">
              <InlineClassField
                classId={danceClass.id}
                field="location"
                value={danceClass.location}
                inputClassName="w-full text-sm"
              />
            </span>
          </div>
        </div>

        {/* Notes */}
        {(danceClass.notes || true) && (
          <p className="text-xs text-primary/80 italic mb-4">
            <InlineClassField
              classId={danceClass.id}
              field="notes"
              value={danceClass.notes}
              multiline
              placeholder="Optional note (e.g. Your first class is free!)"
            />
          </p>
        )}

        {/* Pricing */}
        <div className="border-t border-border/50 pt-4 mb-4">
          {danceClass.is_free ? (
            <div className="text-center">
              <span className="text-2xl font-bold font-display text-green-400">FREE</span>
            </div>
          ) : (
            <div className="grid grid-cols-3 gap-2 text-center">
              <div>
                <p className="text-lg font-bold text-primary">
                  $
                  <InlineClassField
                    classId={danceClass.id}
                    field="price_per_class"
                    value={danceClass.price_per_class}
                    type="number"
                    inputClassName="w-16 text-lg font-bold text-center"
                  />
                </p>
                <p className="text-xs text-muted-foreground">Per Class</p>
              </div>
              <div>
                <p className="text-lg font-bold text-primary">
                  $
                  <InlineClassField
                    classId={danceClass.id}
                    field="price_monthly"
                    value={danceClass.price_monthly}
                    type="number"
                    inputClassName="w-16 text-lg font-bold text-center"
                  />
                </p>
                <p className="text-xs text-muted-foreground">Monthly</p>
              </div>
              <div>
                <p className="text-lg font-bold text-primary">
                  $
                  <InlineClassField
                    classId={danceClass.id}
                    field="price_yearly"
                    value={danceClass.price_yearly}
                    type="number"
                    inputClassName="w-16 text-lg font-bold text-center"
                  />
                </p>
                <p className="text-xs text-muted-foreground">Yearly</p>
              </div>
            </div>
          )}
        </div>

        {/* Register Button */}
        <button
          onClick={() => onRegister(danceClass)}
          className="w-full py-3 bg-primary hover:bg-primary/90 text-primary-foreground rounded-full font-bold transition-all hover:scale-[1.02] flex items-center justify-center gap-2"
          data-testid={`register-class-${danceClass.id}`}
        >
          Register Now
          <ChevronRight className="w-4 h-4" />
        </button>
      </div>
    </motion.div>
  );
};

const PrivateLessonCard = ({ type, title, description, price, note, pathPrefix, onRequest }) => {
  const icons = {
    samba_dance: Footprints,
    samba_drumming: Drum,
    kapoeira: Users,
    percussion: Drum,
    stilt_walking: Users
  };
  const Icon = icons[type] || Music;

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="bg-card border border-border/50 rounded-2xl p-6 hover:border-primary/50 transition-all"
    >
      <div className="flex items-start gap-4">
        <div className="w-12 h-12 rounded-full bg-primary/20 flex items-center justify-center flex-shrink-0">
          <Icon className="w-6 h-6 text-primary" />
        </div>
        <div className="flex-1">
          <h3 className="text-lg font-bold font-display text-foreground mb-1">
            <PageEditable path={`${pathPrefix}.title`} as="span" fallback={title} />
          </h3>
          <p className="text-sm text-muted-foreground mb-2">
            <PageEditable path={`${pathPrefix}.description`} as="span" multiline fallback={description} />
          </p>
          <p className="text-xl font-bold text-primary mb-1">
            <PageEditable path={`${pathPrefix}.price`} as="span" fallback={price} />
          </p>
          {note && (
            <p className="text-xs text-muted-foreground italic">
              <PageEditable path={`${pathPrefix}.note`} as="span" fallback={note} />
            </p>
          )}
        </div>
      </div>
      <button
        onClick={() => onRequest(type, title)}
        className="w-full mt-4 py-2 border border-primary text-primary hover:bg-primary hover:text-primary-foreground rounded-full font-bold transition-all"
        data-testid={`request-private-${type}`}
      >
        <PageEditable path={`${pathPrefix}.cta`} as="span" fallback="Request Appointment" />
      </button>
    </motion.div>
  );
};

const RegistrationModal = ({ danceClass, onClose, onSubmit, onPayment }) => {
  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    email: '',
    phone: '',
    membership_type: 'per_class',
    experience_level: 'beginner',
    emergency_contact: '',
    emergency_phone: '',
    notes: ''
  });
  const [loading, setLoading] = useState(false);
  const [paymentLoading, setPaymentLoading] = useState(false);

  const handleFreeRegistration = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await onSubmit({ ...formData, class_id: danceClass.id });
      onClose();
    } catch (error) {
      console.error('Registration error:', error);
    } finally {
      setLoading(false);
    }
  };

  const handlePayment = async (e) => {
    e.preventDefault();
    if (!formData.first_name || !formData.last_name || !formData.email) {
      toast.error('Please fill in required fields');
      return;
    }
    setPaymentLoading(true);
    try {
      await onPayment({
        class_id: danceClass.id,
        membership_type: formData.membership_type,
        first_name: formData.first_name,
        last_name: formData.last_name,
        email: formData.email,
        phone: formData.phone
      });
    } catch (error) {
      console.error('Payment error:', error);
    } finally {
      setPaymentLoading(false);
    }
  };

  const getPrice = () => {
    if (danceClass.is_free) return 0;
    switch (formData.membership_type) {
      case 'monthly': return danceClass.price_monthly;
      case 'yearly': return danceClass.price_yearly;
      default: return danceClass.price_per_class;
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70" onClick={onClose}>
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        className="bg-card border border-border rounded-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto"
        onClick={e => e.stopPropagation()}
      >
        <div className="p-6 border-b border-border">
          <h2 className="text-2xl font-bold font-display text-foreground">
            <PageEditable path="registration_modal.title" as="span" fallback="Register & Pay" />
          </h2>
          <p className="text-muted-foreground">{danceClass.name}</p>
          <p className="text-sm text-primary">{danceClass.day_of_week} {danceClass.start_time} - {danceClass.end_time}</p>
        </div>

        <form onSubmit={danceClass.is_free ? handleFreeRegistration : handlePayment} className="p-6 space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-foreground mb-1">
                <PageEditable path="registration_modal.first_name_label" as="span" fallback="First Name *" />
              </label>
              <input
                type="text"
                required
                value={formData.first_name}
                onChange={e => setFormData({ ...formData, first_name: e.target.value })}
                className="w-full px-4 py-2 bg-background border border-border rounded-lg focus:ring-2 focus:ring-primary focus:border-primary"
                data-testid="reg-first-name"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-foreground mb-1">
                <PageEditable path="registration_modal.last_name_label" as="span" fallback="Last Name *" />
              </label>
              <input
                type="text"
                required
                value={formData.last_name}
                onChange={e => setFormData({ ...formData, last_name: e.target.value })}
                className="w-full px-4 py-2 bg-background border border-border rounded-lg focus:ring-2 focus:ring-primary focus:border-primary"
                data-testid="reg-last-name"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-foreground mb-1">
              <PageEditable path="registration_modal.email_label" as="span" fallback="Email *" />
            </label>
            <input
              type="email"
              required
              value={formData.email}
              onChange={e => setFormData({ ...formData, email: e.target.value })}
              className="w-full px-4 py-2 bg-background border border-border rounded-lg focus:ring-2 focus:ring-primary focus:border-primary"
              data-testid="reg-email"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-foreground mb-1">
              <PageEditable path="registration_modal.phone_label" as="span" fallback="Phone" />
            </label>
            <input
              type="tel"
              value={formData.phone}
              onChange={e => setFormData({ ...formData, phone: e.target.value })}
              className="w-full px-4 py-2 bg-background border border-border rounded-lg focus:ring-2 focus:ring-primary focus:border-primary"
              data-testid="reg-phone"
            />
          </div>

          {!danceClass.is_free && (
            <div>
              <label className="block text-sm font-medium text-foreground mb-1">
                <PageEditable path="registration_modal.membership_label" as="span" fallback="Select Membership" />
              </label>
              <div className="grid grid-cols-3 gap-2">
                {[
                  { value: 'per_class', label: 'Single Class', price: danceClass.price_per_class },
                  { value: 'monthly', label: 'Monthly', price: danceClass.price_monthly },
                  { value: 'yearly', label: 'Yearly', price: danceClass.price_yearly }
                ].map(option => (
                  <button
                    key={option.value}
                    type="button"
                    onClick={() => setFormData({ ...formData, membership_type: option.value })}
                    className={`p-3 rounded-lg border-2 transition-all ${
                      formData.membership_type === option.value
                        ? 'border-primary bg-primary/10'
                        : 'border-border hover:border-primary/50'
                    }`}
                  >
                    <p className="text-lg font-bold text-primary">${option.price}</p>
                    <p className="text-xs text-muted-foreground">{option.label}</p>
                  </button>
                ))}
              </div>
            </div>
          )}

          {danceClass.is_free ? (
            <div className="p-4 bg-green-500/10 border border-green-500/30 rounded-lg">
              <p className="text-green-400 font-medium text-center">
                <PageEditable path="registration_modal.free_badge" as="span" fallback="This class is FREE!" />
              </p>
            </div>
          ) : (
            <div className="p-4 bg-primary/10 border border-primary/30 rounded-lg">
              <div className="flex justify-between items-center">
                <span className="text-foreground font-medium">
                  <PageEditable path="registration_modal.total_label" as="span" fallback="Total:" />
                </span>
                <span className="text-2xl font-bold text-primary">${getPrice()}</span>
              </div>
            </div>
          )}

          <div className="flex gap-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 py-3 border border-border text-foreground rounded-full font-bold hover:bg-border/20 transition-all"
            >
              <PageEditable path="registration_modal.cancel_cta" as="span" fallback="Cancel" />
            </button>
            <button
              type="submit"
              disabled={loading || paymentLoading}
              className="flex-1 py-3 bg-primary text-primary-foreground rounded-full font-bold hover:bg-primary/90 transition-all disabled:opacity-50 flex items-center justify-center gap-2"
              data-testid="submit-registration"
            >
              {loading || paymentLoading ? (
                <PageEditable path="registration_modal.processing_cta" as="span" fallback="Processing..." />
              ) : danceClass.is_free ? (
                <PageEditable path="registration_modal.register_cta" as="span" fallback="Register Now" />
              ) : (
                <>
                  <PageEditable path="registration_modal.pay_cta_prefix" as="span" fallback="Pay " />${getPrice()}
                  <ChevronRight className="w-4 h-4" />
                </>
              )}
            </button>
          </div>

          {!danceClass.is_free && (
            <p className="text-xs text-center text-muted-foreground">
              <PageEditable path="registration_modal.stripe_note" as="span" fallback="Secure payment powered by Stripe" />
            </p>
          )}
        </form>
      </motion.div>
    </div>
  );
};

const PrivateLessonModal = ({ lessonType, lessonTitle, onClose, onSubmit }) => {
  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    email: '',
    phone: '',
    preferred_date: '',
    preferred_time: '',
    group_size: 1,
    notes: ''
  });
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await onSubmit({ ...formData, lesson_type: lessonType });
      onClose();
    } catch (error) {
      console.error('Request error:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70" onClick={onClose}>
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        className="bg-card border border-border rounded-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto"
        onClick={e => e.stopPropagation()}
      >
        <div className="p-6 border-b border-border">
          <h2 className="text-2xl font-bold font-display text-foreground">
            <PageEditable path="private_modal.title" as="span" fallback="Request Private Lesson" />
          </h2>
          <p className="text-muted-foreground">{lessonTitle}</p>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-foreground mb-1">
                <PageEditable path="private_modal.first_name_label" as="span" fallback="First Name *" />
              </label>
              <input
                type="text"
                required
                value={formData.first_name}
                onChange={e => setFormData({ ...formData, first_name: e.target.value })}
                className="w-full px-4 py-2 bg-background border border-border rounded-lg focus:ring-2 focus:ring-primary focus:border-primary"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-foreground mb-1">
                <PageEditable path="private_modal.last_name_label" as="span" fallback="Last Name *" />
              </label>
              <input
                type="text"
                required
                value={formData.last_name}
                onChange={e => setFormData({ ...formData, last_name: e.target.value })}
                className="w-full px-4 py-2 bg-background border border-border rounded-lg focus:ring-2 focus:ring-primary focus:border-primary"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-foreground mb-1">
              <PageEditable path="private_modal.email_label" as="span" fallback="Email *" />
            </label>
            <input
              type="email"
              required
              value={formData.email}
              onChange={e => setFormData({ ...formData, email: e.target.value })}
              className="w-full px-4 py-2 bg-background border border-border rounded-lg focus:ring-2 focus:ring-primary focus:border-primary"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-foreground mb-1">
              <PageEditable path="private_modal.phone_label" as="span" fallback="Phone *" />
            </label>
            <input
              type="tel"
              required
              value={formData.phone}
              onChange={e => setFormData({ ...formData, phone: e.target.value })}
              className="w-full px-4 py-2 bg-background border border-border rounded-lg focus:ring-2 focus:ring-primary focus:border-primary"
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-foreground mb-1">
                <PageEditable path="private_modal.preferred_date_label" as="span" fallback="Preferred Date" />
              </label>
              <input
                type="date"
                value={formData.preferred_date}
                onChange={e => setFormData({ ...formData, preferred_date: e.target.value })}
                className="w-full px-4 py-2 bg-background border border-border rounded-lg focus:ring-2 focus:ring-primary focus:border-primary"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-foreground mb-1">
                <PageEditable path="private_modal.preferred_time_label" as="span" fallback="Preferred Time" />
              </label>
              <input
                type="time"
                value={formData.preferred_time}
                onChange={e => setFormData({ ...formData, preferred_time: e.target.value })}
                className="w-full px-4 py-2 bg-background border border-border rounded-lg focus:ring-2 focus:ring-primary focus:border-primary"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-foreground mb-1">
              <PageEditable path="private_modal.group_size_label" as="span" fallback="Group Size" />
            </label>
            <input
              type="number"
              min="1"
              value={formData.group_size}
              onChange={e => setFormData({ ...formData, group_size: parseInt(e.target.value) || 1 })}
              className="w-full px-4 py-2 bg-background border border-border rounded-lg focus:ring-2 focus:ring-primary focus:border-primary"
            />
            <p className="text-xs text-muted-foreground mt-1">
              <PageEditable path="private_modal.group_size_note" as="span" fallback="Group rates available for multiple students" />
            </p>
          </div>

          <div>
            <label className="block text-sm font-medium text-foreground mb-1">
              <PageEditable path="private_modal.notes_label" as="span" fallback="Notes / Special Requests" />
            </label>
            <textarea
              value={formData.notes}
              onChange={e => setFormData({ ...formData, notes: e.target.value })}
              rows={3}
              className="w-full px-4 py-2 bg-background border border-border rounded-lg focus:ring-2 focus:ring-primary focus:border-primary resize-none"
            />
          </div>

          <div className="p-4 bg-primary/10 border border-primary/30 rounded-lg">
            <p className="text-sm text-muted-foreground">
              <PageEditable path="private_modal.contact_note" as="span" multiline fallback="We'll contact you within 24-48 hours to confirm your appointment." />
            </p>
          </div>

          <div className="flex gap-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 py-3 border border-border text-foreground rounded-full font-bold hover:bg-border/20 transition-all"
            >
              <PageEditable path="private_modal.cancel_cta" as="span" fallback="Cancel" />
            </button>
            <button
              type="submit"
              disabled={loading}
              className="flex-1 py-3 bg-primary text-primary-foreground rounded-full font-bold hover:bg-primary/90 transition-all disabled:opacity-50"
            >
              {loading
                ? <PageEditable path="private_modal.submitting_cta" as="span" fallback="Submitting..." />
                : <PageEditable path="private_modal.submit_cta" as="span" fallback="Submit Request" />}
            </button>
          </div>
        </form>
      </motion.div>
    </div>
  );
};

const ClassesInner = () => {
  const [searchParams] = useSearchParams();
  const [classes, setClasses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedClass, setSelectedClass] = useState(null);
  const [privateLessonModal, setPrivateLessonModal] = useState(null);
  const [paymentSuccess, setPaymentSuccess] = useState(false);
  const [showTraditionModal, setShowTraditionModal] = useState(false);
  const hero = useHero('classes');

  useEffect(() => {
    fetchClasses();
    
    // Check for payment success
    const payment = searchParams.get('payment');
    const registrationId = searchParams.get('registration');
    
    if (payment === 'success' && registrationId) {
      // Verify payment and show success
      verifyPayment(registrationId);
    } else if (payment === 'cancelled') {
      toast.error('Payment was cancelled. Please try again.');
    }
  }, [searchParams]);

  // Smooth-scroll to hash anchors when navigating from external links (e.g. /classes#private)
  const location = useLocation();
  useEffect(() => {
    if (!location.hash) return;
    const id = location.hash.replace('#', '');
    // Delay so DOM has rendered the sections
    const t = setTimeout(() => {
      const el = document.getElementById(id);
      if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 350);
    return () => clearTimeout(t);
  }, [location.hash]);

  const verifyPayment = async (registrationId) => {
    try {
      const response = await axios.get(`${API}/api/classes/payment-status/${registrationId}`);
      if (response.data.status === 'paid') {
        setPaymentSuccess(true);
        toast.success('Payment successful! You are registered for the class.');
      }
    } catch (error) {
      console.error('Error verifying payment:', error);
    }
  };

  const fetchClasses = async () => {
    try {
      const response = await axios.get(`${API}/api/classes`);
      setClasses(response.data);
    } catch (error) {
      console.error('Error fetching classes:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleRegister = async (formData) => {
    try {
      const response = await axios.post(`${API}/api/classes/register`, formData);
      if (response.data.success) {
        if (response.data.is_free) {
          toast.success('Registration successful! See you in class!');
        } else {
          toast.success('Registration submitted! We\'ll contact you about payment.');
        }
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Registration failed. Please try again.');
      throw error;
    }
  };

  const handlePayment = async (paymentData) => {
    try {
      const response = await axios.post(`${API}/api/classes/checkout`, {
        ...paymentData,
        origin_url: window.location.origin
      });
      
      if (response.data.success && response.data.checkout_url) {
        // Redirect to Stripe checkout
        window.location.href = response.data.checkout_url;
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Payment setup failed. Please try again.');
      throw error;
    }
  };

  const handlePrivateLessonRequest = async (formData) => {
    try {
      const response = await axios.post(`${API}/api/private-lessons/request`, formData);
      if (response.data.success) {
        toast.success('Request submitted! We\'ll contact you within 24-48 hours.');
      }
    } catch (error) {
      toast.error('Failed to submit request. Please try again.');
      throw error;
    }
  };

  const groupedClasses = groupClassesByCategory(classes);

  const privateLessons = [
    {
      type: 'samba_dance',
      title: 'Samba Dance',
      description: 'Private samba dance instruction tailored to your skill level',
      price: '$50/hour',
      note: 'Per person. By appointment only.'
    },
    {
      type: 'samba_drumming',
      title: 'Samba Drumming',
      description: 'Learn authentic Brasilian samba rhythms and drumming techniques',
      price: '$50/hour',
      note: 'Per person. By appointment only.'
    },
    {
      type: 'kapoeira',
      title: 'Kapoeira',
      description: 'Private capoeira instruction for all skill levels',
      price: '$50/hour',
      note: 'Per person. Group rates available.'
    },
    {
      type: 'percussion',
      title: 'Brasilian Percussion',
      description: 'Master the rhythms of Brasil with personalized instruction',
      price: '$50/hour',
      note: 'Group rates available.'
    },
    {
      type: 'stilt_walking',
      title: 'Stilt Walking/Dancing',
      description: 'Learn the art of stilt walking and dancing. We supply the stilts!',
      price: '$75/session',
      note: 'Must be in good physical condition.'
    }
  ];

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background py-12" data-testid="classes-page">
      {/* Payment Success Banner */}
      {paymentSuccess && (
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mb-8">
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-green-500/10 border border-green-500/30 rounded-2xl p-6 flex items-center gap-4"
          >
            <CheckCircle className="w-8 h-8 text-green-400 flex-shrink-0" />
            <div>
              <h3 className="text-lg font-bold text-green-400">
                <PageEditable path="payment_success.title" as="span" fallback="Payment Successful!" />
              </h3>
              <p className="text-green-300/80">
                <PageEditable path="payment_success.body" as="span" multiline fallback="You're registered for the class. We'll see you there!" />
              </p>
            </div>
            <button
              onClick={() => setPaymentSuccess(false)}
              className="ml-auto px-4 py-2 bg-green-500/20 hover:bg-green-500/30 rounded-lg text-green-400 font-medium"
            >
              <PageEditable path="payment_success.dismiss" as="span" fallback="Dismiss" />
            </button>
          </motion.div>
        </div>
      )}

      {/* Hero Section */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mb-12">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center"
        >
          <span className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-primary/10 text-primary text-sm font-medium mb-6">
            <Home className="w-4 h-4" />
            <PageEditable path="hero.eyebrow" as="span" fallback={hero.kicker || "IN-PERSON CLASSES IN NEW ORLEANS"} />
          </span>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold font-display text-foreground mb-4">
            <PageEditable path="hero.title" as="span" fallback={hero.title_top || "Learn from the "} />
            <span className="text-primary"><PageEditable path="hero.title_highlight" as="span" fallback={hero.title_bottom || "Master"} /></span>
          </h1>
          <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
            <PageEditable
              path="hero.subtitle"
              as="span"
              multiline
              fallback={hero.subtitle || "Join Mestre Curtis Pierre in person at our New Orleans studio for authentic Brasilian dance and music classes. Over 38 years of experience teaching Samba, Capoeira, and Brasilian culture."}
            />
          </p>
          <div className="mt-6 flex flex-wrap items-center justify-center gap-3">
            <a
              href="/flyer"
              data-testid="classes-download-flyer-btn"
              className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-yellow-500 hover:bg-yellow-400 text-black font-bold text-sm shadow-lg transition-transform hover:scale-105"
            >
              📄 <PageEditable path="hero.download_flyer_cta" as="span" fallback="Download Class Flyer" />
            </a>
          </div>
        </motion.div>
      </div>

      {/* Read More: Our Tradition Section */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mb-12">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="bg-gradient-to-r from-primary/10 via-amber-500/10 to-red-500/10 border border-primary/30 rounded-2xl p-6"
        >
          <div className="flex flex-col md:flex-row items-start md:items-center gap-6">
            <div className="flex-1">
              <div className="flex items-center gap-3 mb-3">
                <div className="w-12 h-12 rounded-full bg-primary/20 flex items-center justify-center">
                  <Globe className="w-6 h-6 text-primary" />
                </div>
                <div>
                  <h3 className="text-xl font-bold text-foreground">
                    <PageEditable path="tradition.title" as="span" fallback="Authentic Brasilian Tradition" />
                  </h3>
                  <p className="text-sm text-muted-foreground">
                    <PageEditable path="tradition.subtitle" as="span" fallback="The Seven Nations of Afro-Brasilian Culture" />
                  </p>
                </div>
              </div>
              <p className="text-muted-foreground mb-4">
                <PageEditable
                  path="tradition.body"
                  as="span"
                  multiline
                  fallback="Escola de Samba Casa Samba offers the most extensive curriculum in authentic Afro-Brasilian arts, covering traditional rhythms like Samba, Samba Reggae, Samba Afro, Barra Vento, Cabula, Vassi, Ilu, Agabi and dances of the Orixas, Jon-go, Batuque, Cafezal, Maculele, and more."
                />
              </p>
              <button
                onClick={() => setShowTraditionModal(true)}
                className="inline-flex items-center gap-2 px-5 py-2 bg-primary hover:bg-primary/90 text-primary-foreground rounded-full font-semibold transition-all"
                data-testid="read-more-tradition-btn"
              >
                <BookOpen className="w-4 h-4" />
                <PageEditable path="tradition.cta" as="span" fallback="Read More About Our Tradition" />
              </button>
            </div>
            <div className="hidden md:flex flex-col gap-2 text-sm text-muted-foreground">
              <div className="flex items-center gap-2">
                <Award className="w-4 h-4 text-yellow-500" />
                <PageEditable path="tradition.stat_years" as="span" fallback="38+ Years Teaching" />
              </div>
              <div className="flex items-center gap-2">
                <Heart className="w-4 h-4 text-red-500" />
                <PageEditable path="tradition.stat_nations" as="span" fallback="7 Cultural Nations" />
              </div>
              <div className="flex items-center gap-2">
                <Music className="w-4 h-4 text-blue-500" />
                <PageEditable path="tradition.stat_rhythms" as="span" fallback="15+ Traditional Rhythms" />
              </div>
            </div>
          </div>
        </motion.div>
      </div>

      {/* Tradition Modal */}
      <AnimatePresence>
        {showTraditionModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm overflow-y-auto"
            onClick={() => setShowTraditionModal(false)}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0, y: 20 }}
              animate={{ scale: 1, opacity: 1, y: 0 }}
              exit={{ scale: 0.9, opacity: 0, y: 20 }}
              className="bg-card border border-border rounded-2xl w-full max-w-3xl max-h-[90vh] overflow-y-auto my-8"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Modal Header */}
              <div className="sticky top-0 bg-card border-b border-border/50 px-6 py-4 flex items-center justify-between z-10">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-primary/20 flex items-center justify-center">
                    <Globe className="w-5 h-5 text-primary" />
                  </div>
                  <h2 className="text-xl font-bold text-foreground">
                    <PageEditable path="tradition_modal.header" as="span" fallback="Our Brasilian Tradition" />
                  </h2>
                </div>
                <button
                  onClick={() => setShowTraditionModal(false)}
                  className="p-2 hover:bg-white/10 rounded-full transition-colors"
                >
                  <X className="w-6 h-6 text-muted-foreground" />
                </button>
              </div>
              
              {/* Modal Content */}
              <div className="p-6 space-y-8">
                {/* Introduction */}
                <div>
                  <h3 className="text-lg font-bold text-foreground mb-3">
                    <PageEditable path="tradition_modal.intro_title" as="span" fallback="The Most Extensive Brasilian Cultural Curriculum" />
                  </h3>
                  <p className="text-muted-foreground leading-relaxed">
                    <PageEditable
                      path="tradition_modal.intro_body"
                      as="span"
                      multiline
                      fallback="Escola de Samba Casa Samba is dedicated to preserving and teaching the authentic traditions of Afro-Brasilian culture. Our classes go far beyond simple dance steps—we immerse you in the rich history, spiritual significance, and cultural context of each art form. Under the guidance of Mestre Curtis Pierre, you'll learn traditions passed down through generations, representing the Seven Nations of Afro-Brasilian heritage."
                    />
                  </p>
                </div>

                {/* Traditional Rhythms */}
                <div className="bg-background border border-border/50 rounded-xl p-5">
                  <div className="flex items-center gap-3 mb-4">
                    <Drum className="w-6 h-6 text-primary" />
                    <h4 className="text-lg font-bold text-foreground">
                      <PageEditable path="tradition_modal.rhythms_title" as="span" fallback="Traditional Brasilian Rhythms" />
                    </h4>
                  </div>
                  <p className="text-muted-foreground mb-4">
                    <PageEditable
                      path="tradition_modal.rhythms_body"
                      as="span"
                      multiline
                      fallback="Master the authentic percussion rhythms that form the heartbeat of Brasilian culture:"
                    />
                  </p>
                  <EditableList
                    path="tradition_modal.rhythms"
                    fallback={['Samba', 'Samba Reggae', 'Samba Afro', 'Barra Vento', 'Cabula', 'Vassi', 'Ilu', 'Agabi', 'Ijexá', 'Alujá', 'Batucada', 'Maracatu']}
                    emptyItem={() => 'New rhythm'}
                    className="grid grid-cols-2 md:grid-cols-3 gap-3"
                    renderItem={(rhythm, idx) => (
                      <div className="flex items-center gap-2 px-3 py-2 bg-primary/10 rounded-lg h-full">
                        <Music className="w-4 h-4 text-primary flex-shrink-0" />
                        <PageEditable
                          path={`tradition_modal.rhythms.${idx}`}
                          as="span"
                          fallback={rhythm}
                          className="text-sm font-medium text-foreground"
                        />
                      </div>
                    )}
                  />
                </div>

                {/* Traditional Dances */}
                <div className="bg-background border border-border/50 rounded-xl p-5">
                  <div className="flex items-center gap-3 mb-4">
                    <Footprints className="w-6 h-6 text-primary" />
                    <h4 className="text-lg font-bold text-foreground">
                      <PageEditable path="tradition_modal.dances_title" as="span" fallback="Afro-Brasilian Dances" />
                    </h4>
                  </div>
                  <p className="text-muted-foreground mb-4">
                    <PageEditable
                      path="tradition_modal.dances_body"
                      as="span"
                      multiline
                      fallback="Experience the spiritual and celebratory dances of Brasil's African heritage:"
                    />
                  </p>
                  <EditableList
                    path="tradition_modal.dances"
                    fallback={['Dances of the Orixas', 'Jon-go', 'Batuque', 'Cafezal', 'Maculelê', 'Samba de Roda', 'Samba no Pé', 'Afoxé', 'Coco']}
                    emptyItem={() => 'New dance'}
                    className="grid grid-cols-2 md:grid-cols-3 gap-3"
                    renderItem={(dance, idx) => (
                      <div className="flex items-center gap-2 px-3 py-2 bg-amber-500/10 rounded-lg h-full">
                        <Star className="w-4 h-4 text-amber-500 flex-shrink-0" />
                        <PageEditable
                          path={`tradition_modal.dances.${idx}`}
                          as="span"
                          fallback={dance}
                          className="text-sm font-medium text-foreground"
                        />
                      </div>
                    )}
                  />
                </div>

                {/* Seven Nations */}
                <div className="bg-gradient-to-r from-primary/10 to-amber-500/10 border border-primary/30 rounded-xl p-5">
                  <div className="flex items-center gap-3 mb-4">
                    <Heart className="w-6 h-6 text-red-500" />
                    <h4 className="text-lg font-bold text-foreground">
                      <PageEditable path="tradition_modal.nations_title" as="span" fallback="The Seven Nations" />
                    </h4>
                  </div>
                  <p className="text-muted-foreground mb-4">
                    <PageEditable
                      path="tradition_modal.nations_body"
                      as="span"
                      multiline
                      fallback="Our curriculum honors the cultural contributions of the Seven Nations of Afro-Brasilian heritage—the distinct African ethnic groups whose traditions merged in Brasil to create the rich tapestry of music, dance, and spiritual practices we teach today. Each rhythm and dance carries the legacy of these ancestral cultures."
                    />
                  </p>
                  <ul className="space-y-2 text-muted-foreground">
                    <li className="flex items-start gap-2">
                      <CheckCircle className="w-4 h-4 text-green-500 mt-1 flex-shrink-0" />
                      <PageEditable path="tradition_modal.bullet_1" as="span" multiline fallback="Understanding the historical context and cultural significance of each art form" />
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle className="w-4 h-4 text-green-500 mt-1 flex-shrink-0" />
                      <PageEditable path="tradition_modal.bullet_2" as="span" multiline fallback="Learning proper techniques passed down through generations of masters" />
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle className="w-4 h-4 text-green-500 mt-1 flex-shrink-0" />
                      <PageEditable path="tradition_modal.bullet_3" as="span" multiline fallback="Experiencing the spiritual and communal aspects of Brasilian culture" />
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle className="w-4 h-4 text-green-500 mt-1 flex-shrink-0" />
                      <PageEditable path="tradition_modal.bullet_4" as="span" multiline fallback="Connecting rhythm, movement, and cultural expression as one unified practice" />
                    </li>
                  </ul>
                </div>

                {/* Why Casa Samba */}
                <div>
                  <h4 className="text-lg font-bold text-foreground mb-3">
                    <PageEditable path="tradition_modal.why_title" as="span" fallback="Why Learn at Escola de Samba Casa Samba?" />
                  </h4>
                  <p className="text-muted-foreground leading-relaxed">
                    <PageEditable
                      path="tradition_modal.why_body"
                      as="span"
                      multiline
                      fallback="With over 38 years of experience, Mestre Curtis Pierre brings an unparalleled depth of knowledge to every class. Unlike surface-level instruction found elsewhere, Escola de Samba Casa Samba offers complete immersion in authentic tradition. Whether you're a beginner or advanced practitioner, you'll gain not just skills, but a true understanding of the culture behind the art."
                    />
                  </p>
                </div>
              </div>
              
              {/* Modal Footer */}
              <div className="sticky bottom-0 bg-card border-t border-border/50 px-6 py-4">
                <button
                  onClick={() => setShowTraditionModal(false)}
                  className="w-full py-3 bg-primary hover:bg-primary/90 text-primary-foreground rounded-xl font-semibold flex items-center justify-center gap-2 transition-colors"
                >
                  <PageEditable path="tradition_modal.back_cta" as="span" fallback="Back to Classes" />
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* In-Person Location Banner (Prominent) */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mb-12">
        <div className="bg-gradient-to-r from-primary/20 via-amber-500/10 to-primary/20 border-2 border-primary/40 rounded-2xl p-6 shadow-glow">
          <div className="flex flex-col md:flex-row items-center justify-center gap-4 text-center md:text-left">
            <div className="flex items-center gap-3">
              <div className="w-14 h-14 rounded-full bg-primary/20 flex items-center justify-center flex-shrink-0">
                <MapPin className="w-7 h-7 text-primary" />
              </div>
              <div>
                <p className="text-xs uppercase tracking-wider text-primary font-bold mb-1">
                  <PageEditable path="location.tag" as="span" fallback="IN-PERSON · NEW ORLEANS" />
                </p>
                <p className="text-foreground font-bold text-lg">
                  <PageEditable path="location.name" as="span" fallback="Escola de Samba Casa Samba" />
                </p>
                <p className="text-muted-foreground">
                  <PageEditable path="location.address" as="span" fallback="800 Race Street, New Orleans, LA 70130" />
                </p>
              </div>
            </div>
            <div className="hidden md:block h-14 w-px bg-border/50 mx-2" />
            <div className="text-sm text-muted-foreground max-w-xs">
              <PageEditable
                path="location.blurb"
                as="span"
                multiline
                fallback="All Adult, Kids and Private classes below are taught in person at our New Orleans studio."
              />
            </div>
          </div>
        </div>
      </div>

      {/* Quick Jump Nav (anchors to sections below - all sections render at once) */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mb-8">
        <div className="flex flex-wrap justify-center gap-3">
          {[
            { id: 'adult', label: 'In-Person Adult Classes', icon: Users },
            { id: 'kids', label: 'In-Person Samba Kids', icon: Star },
            { id: 'private', label: 'Private Lessons', icon: Music }
          ].map((tab, idx) => (
            <a
              key={tab.id}
              href={`#${tab.id}`}
              onClick={(e) => {
                e.preventDefault();
                const el = document.getElementById(tab.id);
                if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
              }}
              className="flex items-center gap-2 px-6 py-3 rounded-full font-bold transition-all bg-card border border-border text-muted-foreground hover:text-foreground hover:border-primary/50 hover:bg-primary/5"
              data-testid={`jump-${tab.id}`}
            >
              <tab.icon className="w-4 h-4" />
              <PageEditable path={`tabs.${idx}.label`} as="span" fallback={tab.label} />
            </a>
          ))}
        </div>
      </div>

      {/* Content - ALL sections render at once so nothing is hidden behind tabs */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 space-y-16">
        {/* Adult Classes */}
        <section id="adult" className="scroll-mt-24">
          <span id="in-person-lessons" className="block -mt-24 pt-24" aria-hidden="true" />
          <div>
            <div className="mb-8">
              <div className="flex items-center gap-2 mb-2">
                <span className="inline-flex items-center gap-1 px-2 py-1 rounded-md bg-primary/15 text-primary text-xs font-bold uppercase tracking-wider">
                  <Home className="w-3 h-3" /> <PageEditable path="adult.chip" as="span" fallback="In-Person" />
                </span>
                <h2 className="text-2xl font-bold font-display text-foreground">
                  <PageEditable path="adult.heading" as="span" fallback="Weekly In-Person Classes" />
                </h2>
              </div>
              <p className="text-muted-foreground">
                <PageEditable
                  path="adult.description"
                  as="span"
                  multiline
                  fallback="Held every week at 800 Race Street, New Orleans. Your first class is FREE! Walk-ins welcome, no registration required."
                />
              </p>
            </div>
            
            {groupedClasses.adult.length > 0 ? (
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                {sortByDay(groupedClasses.adult).map(danceClass => (
                  <ClassCard
                    key={danceClass.id}
                    danceClass={danceClass}
                    onRegister={setSelectedClass}
                  />
                ))}
              </div>
            ) : (
              <div className="text-center py-12 bg-card border border-border/50 rounded-2xl">
                <Calendar className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                <p className="text-muted-foreground">
                  <PageEditable path="adult.empty_state" as="span" multiline fallback="No classes scheduled yet. Check back soon!" />
                </p>
              </div>
            )}

            {/* Pricing Info */}
            <div className="mt-12 bg-card border border-border/50 rounded-2xl p-8">
              <h3 className="text-xl font-bold font-display text-foreground mb-6 text-center">
                <PageEditable path="pricing.heading" as="span" fallback="Membership Options" />
              </h3>
              <div className="grid md:grid-cols-4 gap-6">
                <div className="text-center">
                  <p className="text-3xl font-bold text-primary">
                    <PageEditable path="pricing.tier1_price" as="span" fallback="$10" />
                  </p>
                  <p className="text-sm text-muted-foreground">
                    <PageEditable path="pricing.tier1_label" as="span" fallback="Per Class" />
                  </p>
                  <p className="text-xs text-muted-foreground mt-1">
                    <PageEditable path="pricing.tier1_note" as="span" fallback="Walk-ins welcome" />
                  </p>
                </div>
                <div className="text-center">
                  <p className="text-3xl font-bold text-primary">
                    <PageEditable path="pricing.tier2_price" as="span" fallback="$45" />
                  </p>
                  <p className="text-sm text-muted-foreground">
                    <PageEditable path="pricing.tier2_label" as="span" fallback="Monthly" />
                  </p>
                  <p className="text-xs text-muted-foreground mt-1">
                    <PageEditable path="pricing.tier2_note" as="span" fallback="Bring a friend, get $10 off!" />
                  </p>
                </div>
                <div className="text-center">
                  <p className="text-3xl font-bold text-primary">
                    <PageEditable path="pricing.tier3_price" as="span" fallback="$150" />
                  </p>
                  <p className="text-sm text-muted-foreground">
                    <PageEditable path="pricing.tier3_label" as="span" fallback="Yearly (Single Class)" />
                  </p>
                  <p className="text-xs text-muted-foreground mt-1">
                    <PageEditable path="pricing.tier3_note" as="span" fallback="50% off with a friend!" />
                  </p>
                </div>
                <div className="text-center">
                  <p className="text-3xl font-bold text-primary">
                    <PageEditable path="pricing.tier4_price" as="span" fallback="$250" />
                  </p>
                  <p className="text-sm text-muted-foreground">
                    <PageEditable path="pricing.tier4_label" as="span" fallback="Yearly (Both Classes)" />
                  </p>
                  <p className="text-xs text-muted-foreground mt-1">
                    <PageEditable path="pricing.tier4_note" as="span" fallback="Samba + Kapoeira" />
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Kids Classes */}
        <section id="kids" className="scroll-mt-24">
          <div>
            <div className="mb-8 bg-gradient-to-r from-primary/20 to-transparent border border-primary/30 rounded-2xl p-8">
              <div className="flex items-center gap-2 mb-3">
                <span className="inline-flex items-center gap-1 px-2 py-1 rounded-md bg-primary/15 text-primary text-xs font-bold uppercase tracking-wider">
                  <Home className="w-3 h-3" /> <PageEditable path="kids.chip" as="span" fallback="In-Person" />
                </span>
                <h2 className="text-2xl font-bold font-display text-foreground">
                  <PageEditable path="kids.heading" as="span" fallback="Samba Kids Program" />
                </h2>
              </div>
              <p className="text-muted-foreground mb-4">
                <PageEditable
                  path="kids.description"
                  as="span"
                  multiline
                  fallback="Building good character through drumming, dancing, and singing Brasilian Culture! Children learn about Respect, Responsibility, and Fairness while having BIG FUN. Held in person at our New Orleans studio."
                />
              </p>
              <div className="flex items-center gap-2 text-green-400">
                <Star className="w-5 h-5" />
                <span className="font-bold">
                  <PageEditable path="kids.free_note" as="span" fallback="FREE for all children under 18!" />
                </span>
              </div>
            </div>

            {groupedClasses.kids.length > 0 ? (
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                {sortByDay(groupedClasses.kids).map(danceClass => (
                  <ClassCard
                    key={danceClass.id}
                    danceClass={danceClass}
                    onRegister={setSelectedClass}
                  />
                ))}
              </div>
            ) : (
              <div className="text-center py-12 bg-card border border-border/50 rounded-2xl">
                <Star className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                <p className="text-muted-foreground">
                  <PageEditable path="kids.empty_state" as="span" multiline fallback="Kids classes coming soon!" />
                </p>
              </div>
            )}

            {/* What Kids Learn */}
            <div className="mt-12 grid md:grid-cols-3 gap-6">
              <div className="bg-card border border-border/50 rounded-2xl p-6">
                <h4 className="font-bold text-foreground mb-2">
                  <PageEditable path="kids_learn.music_title" as="span" fallback="Music Theory" />
                </h4>
                <p className="text-sm text-muted-foreground">
                  <PageEditable
                    path="kids_learn.music_body"
                    as="span"
                    multiline
                    fallback="Basic understanding of music, rudimental drumming techniques, and samba rhythms."
                  />
                </p>
              </div>
              <div className="bg-card border border-border/50 rounded-2xl p-6">
                <h4 className="font-bold text-foreground mb-2">
                  <PageEditable path="kids_learn.culture_title" as="span" fallback="Cultural Education" />
                </h4>
                <p className="text-sm text-muted-foreground">
                  <PageEditable
                    path="kids_learn.culture_body"
                    as="span"
                    multiline
                    fallback="History of Samba, identification of instruments, and Brasilian dance movements."
                  />
                </p>
              </div>
              <div className="bg-card border border-border/50 rounded-2xl p-6">
                <h4 className="font-bold text-foreground mb-2">
                  <PageEditable path="kids_learn.health_title" as="span" fallback="Health & Confidence" />
                </h4>
                <p className="text-sm text-muted-foreground">
                  <PageEditable
                    path="kids_learn.health_body"
                    as="span"
                    multiline
                    fallback="Physical fitness, confidence building, and self-esteem through movement."
                  />
                </p>
              </div>
            </div>

            {/* Family Note */}
            <div className="mt-8 bg-card border border-border/50 rounded-2xl p-6 text-center">
              <p className="text-muted-foreground">
                <PageEditable
                  path="kids.family_note"
                  as="span"
                  multiline
                  fallback="Family Friendly: Parents can take adult Samba classes while kids are in their class - same location, same time!"
                />
              </p>
            </div>
          </div>
        </section>

        {/* Private Lessons */}
        <section id="private" className="scroll-mt-24">
          <span id="private-lessons" className="block -mt-24 pt-24" aria-hidden="true" />
          <div>
            <div className="mb-8">
              <div className="flex items-center gap-2 mb-2">
                <span className="inline-flex items-center gap-1 px-2 py-1 rounded-md bg-primary/15 text-primary text-xs font-bold uppercase tracking-wider">
                  <Home className="w-3 h-3" /> <PageEditable path="private.chip" as="span" fallback="In-Person or Small Group" />
                </span>
                <h2 className="text-2xl font-bold font-display text-foreground">
                  <PageEditable path="private.heading" as="span" fallback="Private Instruction" />
                </h2>
              </div>
              <p className="text-muted-foreground">
                <PageEditable
                  path="private.description"
                  as="span"
                  multiline
                  fallback="One-on-one or small group in-person lessons by appointment. Call 504-236-7479 to schedule."
                />
              </p>
            </div>

            <div className="grid md:grid-cols-2 gap-6">
              {privateLessons.map((lesson, idx) => (
                <PrivateLessonCard
                  key={lesson.type}
                  {...lesson}
                  pathPrefix={`private_lessons.${lesson.type}`}
                  onRequest={(type, title) => setPrivateLessonModal({ type, title })}
                />
              ))}
            </div>
          </div>
        </section>
      </div>

      {/* Registration Modal */}
      {selectedClass && (
        <RegistrationModal
          danceClass={selectedClass}
          onClose={() => setSelectedClass(null)}
          onSubmit={handleRegister}
          onPayment={handlePayment}
        />
      )}

      {/* Private Lesson Modal */}
      {privateLessonModal && (
        <PrivateLessonModal
          lessonType={privateLessonModal.type}
          lessonTitle={privateLessonModal.title}
          onClose={() => setPrivateLessonModal(null)}
          onSubmit={handlePrivateLessonRequest}
        />
      )}
    </div>
  );
};

import { PageEditProvider } from '@/context/PageEditModeContext';
import { PageEditToolbar } from '@/components/edit/PageEditToolbar';
import { EditableList } from '@/components/edit/EditableList';
import AdminEditorLink from '@/components/edit/AdminEditorLink';

export const Classes = () => (
  <PageEditProvider slug="classes">
    <ClassesInner />
    <PageEditToolbar />
    <AdminEditorLink to="/admin/dashboard#class-editor-btn" label="Edit Classes" />
  </PageEditProvider>
);
